<script setup>
import { computed, ref } from 'vue';
import { CopyDocument } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';

const { rest_url } = window.fvGlobalVar;
const mcpUrl = computed(() => `${rest_url}form-vibes-server/mcp`);

const configCode = computed(() => {
  return `"Form Vibes": {
  "command": "npx",
  "args": [
    "-y",
    "@automattic/mcp-wordpress-remote"
  ],
  "env": {
    "WP_API_URL": "${mcpUrl.value}",
    "WP_API_USERNAME": "", 
    "WP_API_PASSWORD": "" 
  }
}`;
});

const copyToClipboard = () => {
  navigator.clipboard.writeText(configCode.value).then(() => {
    ElNotification({
        offset: 30,
        dangerouslyUseHTMLString: true,
        message: `<strong>Configuration copied to clipboard!</strong>`,
        type: 'success',
        class:'fv-items-center'
    })
  });
};
</script>

<template>
  <div class="mcp-server-config">
    <el-row :gutter="40">
      <el-col :span="14">
        <div class="config-section">
          <h3>Claude Configuration</h3>
          <p class="section-desc">Copy the following configuration block and add it to your Claude Desktop configuration file.</p>
          <div class="code-block-wrapper">
            <pre class="code-block">{{ configCode }}</pre>
            <el-button 
              class="copy-btn" 
              type="primary" 
              size="small"
              :icon="CopyDocument" 
              @click="copyToClipboard"
            >
              Copy Config
            </el-button>
          </div>
          <div class="env-info">
            <p><strong>Note:</strong> You must fill in the following environment variables in the config file:</p>
            <ul>
              <li><code>WP_API_USERNAME</code>: Your WordPress username.</li>
              <li><code>WP_API_PASSWORD</code>: Your WordPress Application Password (generated in Profile).</li>
            </ul>
          </div>
        </div>
      </el-col>
      <el-col :span="10">
        <div class="instructions-section">
          <h3>Step-by-Step Instructions to Implement</h3>
          
          <div class="instruction-group">
            <h4>Step 1: Install the MCP Adapter Plugin</h4>
            <p>Before configuring Claude, you must install and activate the MCP Adapter plugin.</p>
            <p>Download the <a href="https://github.com/WordPress/mcp-adapter" target="_blank">MCP Adapter</a> plugin from the official GitHub repository.</p>
            <p>Install and activate the plugin on your WordPress site.</p>
            <p>After activating the MCP Adapter plugin, run the <code>composer install</code> command inside the plugin directory to install Composer dependencies.</p>
          </div>

          <div class="instruction-group">
            <h4>Step 2: Add MCP Configuration in Claude</h4>
            <p>Now, configure Claude Desktop to connect with the Form Vibes MCP server.</p>
            <ol>
              <li>Open the Claude Desktop app.</li>
              <li>Go to Settings (gear icon).</li>
              <li>Select Developer from the sidebar.</li>
              <li>Click Edit Config — this will open the <code>claude_desktop_config.json</code> file.</li>
              <li>Copy the configuration block displayed on the left panel of this page.</li>
              <li>Paste it inside the <code>mcpServers</code> object.</li>
              <li>Update the following environment variables:
                <ul>
                  <li><code>WP_API_USERNAME</code> → Your WordPress username</li>
                  <li><code>WP_API_PASSWORD</code> → The Application Password you generate</li>
                </ul>
              </li>
              <li>Save the file and restart Claude Desktop.</li>
            </ol>
          </div>

          <div class="instruction-group">
            <h4>Step 3: Generate WordPress API Credentials</h4>
            <ol>
              <li>Log in to your WordPress Admin Dashboard.</li>
              <li>Go to Users → Profile.</li>
              <li>Scroll down to the Application Passwords section.</li>
              <li>Enter a name (for example: Claude MCP).</li>
              <li>Click Add New Application Password.</li>
              <li>Copy the generated 24-character password and use it as the value for the <code>WP_API_PASSWORD</code> variable in your config file.<br>
              <em> <b>(You won’t be able to view it again, so make sure to save it securely.)</b></em></li>
            </ol>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<style scoped>
.mcp-server-config {
  padding: 24px;
  background: #fff;
  border: 1px solid #dcdcde;
  border-radius: 4px;
}
.config-section h3, .instructions-section h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1d2327;
}
.section-desc {
  color: #646970;
  margin-bottom: 20px;
}
.code-block-wrapper {
  position: relative;
  background: #f6f7f7;
  border: 1px solid #dcdcde;
  border-radius: 4px;
  padding: 20px;
  margin-bottom: 20px;
}
.code-block {
  margin: 0;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #2c3338;
  white-space: pre-wrap;
  word-break: break-all;
}
.copy-btn {
  position: absolute;
  top: 10px;
  right: 10px;
}
.env-info {
  background: #f0f6fb;
  border-left: 4px solid #2271b1;
  padding: 12px 16px;
  font-size: 13px;
}
.env-info p {
  margin-top: 0;
  margin-bottom: 8px;
}
.env-info ul {
  margin: 0;
  padding-left: 20px;
}
.instruction-group {
  margin-bottom: 30px;
}
.instruction-group h4 {
  margin-bottom: 12px;
  font-size: 1rem;
  color: #2c3338;
  border-bottom: 1px solid #dcdcde;
  padding-bottom: 8px;
}
.instruction-group ol {
  padding-left: 20px;
  margin: 0;
}
.instruction-group li {
  margin-bottom: 12px;
  color: #475056;
  line-height: 1.6;
  font-size: 13px;
}
</style>
